import { Address } from './addresses';

export type PropertyId = number;
export type PropertySlug = string;
export type PhotoId = number;

export interface Photo {
  id: PhotoId;
  url: string;
  position: number;
}

export interface PhotoPost {
  file: any;
}

export interface PhotoPatch {
  position?: number;
}

export enum PropertyType {
  NONE = 'NONE',
  SINGLE_FAMILY_HOME = 'SINGLE_FAMILY_HOME',
  MULTI_FAMILY_HOME = 'MULTI_FAMILY_HOME',
  APARTMENT = 'APARTMENT',
}

export enum UsageType {
  LONG_TERM_RENTAL = 'LONG_TERM_RENTAL',
  SHORT_TERM_RENTAL = 'SHORT_TERM_RENTAL',
}

export enum LeaseStatus {
  /**
   * Properties that haven’t closed yet
   */
  UNDER_CONTRACT = 'UNDER_CONTRACT',

  /**
   * Seller post close occupancy
   */
  OCCUPIED_PCO = 'OCCUPIED_PCO',

  /**
   * Initial rehab/turn
   *
   * Prop is vacant, not rented (ie no lease signed), and undergoing rehab, turn, or maintenance work.
   *
   * Repairs etc prior to home being listed for rent.
   */
  VACANT_UNRENTED_NOT_READY = 'VACANT_UNRENTED_NOT_READY',

  /**
   * Approved application
   *
   * Prop is vacant, rented (ie lease signed, hold fee paid, general good chance of successful occupancy but not
   * yet guaranteed), and QA (quality assurance) complete (ie rent ready or ready for tenants to move in)
   *
   * Majority of the time this changes to Occupied status upon tenant move-in, but NOT ALWAYS so we must be
   * careful with messaging as we want to avoid falsely communicating with investors of a leased property
   * that still may fall through.
   */
  VACANT_RENTED_READY = 'VACANT_RENTED_READY',

  /**
   * Approved application
   *
   * Prop is vacant, rented (ie lease signed, hold fee paid), but still undergoing rehab, turn, or maintenance
   * work preventing tenants from moving in.
   *
   * NOTE: this is the least common for Arrived today as we are not actively pre-leasing many homes.
   */
  VACANT_RENTED_NOT_READY = 'VACANT_RENTED_NOT_READY',

  /**
   * Property is listed for rent
   *
   * Prop is vacant, not rented (ie no lease signed), and open for leasing (ie prospective tenants can book tours of
   * home and all rehab, turn, and maintenance work is complete)
   *
   * Exceptions could be the rehab, turn, or maintenance work is mostly complete and the PM is still working on
   * non-critical items (ex: blinds install, waiting for fridge delivery, etc) but home is clean, safe,
   * and functional for lease up
   */
  VACANT_UNRENTED_READY = 'VACANT_UNRENTED_READY',

  /**
   * Property is rented by a rent-paying tenant
   */
  OCCUPIED = 'OCCUPIED',

  /**
   * Tenant turnover
   */
  NOTICE = 'NOTICE',

  /**
   * Tenant turnover
   */
  EVICTION = 'EVICTION',

  FOR_SALE = 'FOR_SALE',

  CANCELLED_TRANSACTION = 'CANCELLED_TRANSACTION',

  /**
   * @deprecated
   */
  RENTED = 'RENTED',

  /**
   * @deprecated
   */
  LISTED = 'LISTED',

  /**
   * @deprecated
   */
  UNLISTED = 'UNLISTED',
}

export interface Property {
  id: PropertyId;
  slug?: PropertySlug;
  propertyType?: PropertyType;
  description?: string;
  youtubeWalkthroughVideoId?: string;
  matterportTourId?: string;
  address?: Address;
  photos: Photo[];
  squareFootage?: number;
  bedrooms?: number;
  fullBathrooms?: number;
  halfBathrooms?: number;
  yearBuilt?: number;
  purchasePrice?: number;
  currentValue?: number;
  rent?: number;
  rentbackPercent?: number;
  securityDeposit?: number;
  desiredLeaseLengthMonths?: number;
  availabilityDate?: Date;
  petsAllowed?: boolean;
  leaseStatus: LeaseStatus;
  leaseStartAt?: Date;
  leaseEndAt?: Date;
  createdAt?: Date;
  updatedAt?: Date;
  listed?: boolean;
  arrivedOwned?: boolean;
  active?: boolean;
}

export const getLeaseStatusMessageDisplayText = (leaseStatus: LeaseStatus | undefined): string => {
  return leaseStatus ? LEASE_STATUS_MESSAGES_STRINGS[leaseStatus].display.text : 'N/A';
};

/***
 * Not all Lease Statuses have accompanying tooltips.
 */
interface LeaseStatusMessageStrings {
  display: { id: string; text: string };
  tooltip?: { id: string; text: string };
}

export const isLeaseStatusRented = (leaseStatus?: LeaseStatus): boolean => {
  switch (leaseStatus) {
    case LeaseStatus.NOTICE:
    case LeaseStatus.OCCUPIED:
      return true;
    default:
      return false;
  }
};

export const LEASE_STATUS_MESSAGES_STRINGS: Record<LeaseStatus, LeaseStatusMessageStrings> = {
  CANCELLED_TRANSACTION: {
    display: { id: 'lease-status-cancelled-transaction', text: 'Cancelled Transaction' },
  },
  EVICTION: {
    display: { id: 'lease-status-eviction', text: 'Rented' },
  },
  FOR_SALE: {
    display: { id: 'lease-status-for-sale', text: 'For Sale' },
  },
  NOTICE: {
    display: { id: 'lease-status-notice', text: 'Rented' },
  },
  OCCUPIED: {
    display: { id: 'lease-status-occupied', text: 'Rented' },
  },
  OCCUPIED_PCO: {
    display: {
      id: 'lease-status-occupied-pco',
      text: 'Seller post-closing occupancy',
    },
    tooltip: {
      id: 'lease-status-occupied-pco-tooltip',
      text: 'A post-closing occupancy agreement has been negotiated with the sellers.',
    },
  },
  UNDER_CONTRACT: {
    display: {
      id: 'lease-status-under-contract',
      text: 'Under contract',
    },
    tooltip: {
      id: 'lease-status-under-contract-tooltip',
      text: 'This property has a signed purchase contract and is awaiting closing.',
    },
  },
  VACANT_UNRENTED_NOT_READY: {
    display: {
      id: 'lease-status-vacant-unrented-not-ready',
      text: 'Pre-market preparation',
    },
    tooltip: {
      id: 'lease-status-vacant-unrented-not-ready-tooltip',
      text: 'The property management team is preparing the property for the market (rehab/turn work, maintenance, photos, etc.)',
    },
  },
  VACANT_UNRENTED_READY: {
    display: { id: 'lease-status-vacant-unrented-ready', text: 'Marketed for rent' },
    tooltip: {
      id: 'lease-status-vacant-unrented-ready-tooltip',
      text: 'This property is currently listed for rent on Zillow and other major rental sites.',
    },
  },
  VACANT_RENTED_NOT_READY: {
    display: { id: 'lease-status-vacant-rented-not-ready', text: 'Approved rental application' },
    tooltip: {
      id: 'lease-status-vacant-rented-not-ready-tooltip',
      text: 'The property has an approved lease application and is awaiting the tenant’s move in.',
    },
  },
  VACANT_RENTED_READY: {
    display: { id: 'lease-status-vacant-rented-ready', text: 'Approved rental application' },
    tooltip: {
      id: 'lease-status-vacant-rented-ready-tooltip',
      text: 'The property has an approved lease application and is awaiting the tenant’s move in.',
    },
  },
  // legacy
  RENTED: {
    display: { id: 'lease-status-rented', text: 'Rented' },
  },
  LISTED: {
    display: { id: 'lease-status-listed', text: 'Marketed for rent' },
  },
  UNLISTED: {
    display: { id: 'lease-status-unlisted', text: 'Pre-market preparation' },
  },
};
